import type { Command } from './Command';

/**
 * Several commands treated as one history entry.
 *
 * The wand is the reason this exists: one sentence ("cut a swale here and plant it with switchgrass")
 * legitimately sculpts the ground, paints it, and adds thirty plants. Those are three different kinds
 * of edit against three different owners, but they are *one thing the user asked for*, and a Ctrl+Z
 * that took back only the planting would leave a swale nobody asked to keep.
 *
 * Undo runs the parts in reverse order, which matters whenever a later part was built on an earlier
 * one — planting is placed against sculpted ground, so the plants come out before the ground moves
 * back under them.
 */
export class CompositeCommand implements Command {
  readonly label: string;
  readonly kind = 'composite' as const;
  private readonly parts: readonly Command[];

  constructor(parts: readonly Command[], label: string) {
    this.parts = [...parts];
    this.label = label;
  }

  /** True when there is nothing to record — the caller should not push this onto the history. */
  get isEmpty(): boolean {
    return this.parts.length === 0;
  }

  get size(): number {
    return this.parts.length;
  }

  execute(): void {
    for (const part of this.parts) part.execute();
  }

  undo(): void {
    for (let i = this.parts.length - 1; i >= 0; i -= 1) {
      this.parts[i]?.undo();
    }
  }
}
